/**
 * The milestone board's one request: a project's phases as columns with their
 * tasks already nested inside them.
 *
 * Its own file rather than another section of `lib/projects.ts`, which is at
 * eight hundred lines and already describes the register, the summary, the
 * milestone writes, the time entries, the expenses and the contracts
 * (AGENTS.md §4). The depth ring added `crmAttachments.ts`, `projectActivity.ts`
 * and `taskChecklist.ts` the same way.
 *
 * `Milestone` and `Task` are IMPORTED rather than redeclared. The nested task is
 * the task register's own row shape - the server builds it with the register's
 * helper for exactly that reason - so a second interface here would be a copy
 * that drifts the first time a column is added, and the board would quietly stop
 * drawing a badge that works "on the other screen".
 *
 * ## What the endpoint promises, and what a caller must not re-derive
 *
 * The columns arrive in the order the board draws them, with the unfiled column
 * LAST and ALWAYS present - even when it is empty, and even when the project has
 * no phases at all. That column is not a leftover to be inferred by subtracting
 * the phases from a total: `Task.milestoneId` is nullable and its foreign key is
 * SET NULL, so a task under no phase is an ordinary task and deleting a
 * milestone deliberately makes more of them. A client that filtered it out would
 * show fewer tasks than the project has, with nothing on screen admitting it.
 *
 * `total` and `done` are the server's tallies and are NOT always the length of
 * `tasks`. On a project past the endpoint's card cap they count the whole
 * column while `tasks` holds a prefix, which is what `truncated` is for. So the
 * header count is read from these fields rather than from `tasks.length`, and a
 * screen that moves a card between columns adjusts both numbers itself.
 */
import { api } from "./api.js";
import type { Milestone, Task } from "./projects.js";

/** One column: a phase with its tasks, or the tasks belonging to no phase. */
export interface MilestoneBoardColumn {
  /** Null on exactly one column - the unfiled one. That is how it is told apart,
   *  rather than by matching a magic id. */
  milestoneId: string | null;
  /** The phase itself, so the column header needs no client-side join. Null on
   *  the unfiled column, which the caller names in its own language. */
  milestone: Milestone | null;
  tasks: Task[];
  /** Tasks in this column, all statuses - the server's count, not `tasks.length`. */
  total: number;
  /** How many of those are done. The header's "3 / 7" is `done` over `total`. */
  done: number;
}

export interface MilestoneBoardData {
  /** The project's phases in board order, without the unfiled column. The move
   *  menu on a card is built from this rather than from a project loaded
   *  earlier, so the phases offered are the phases the board is drawing. */
  rows: Milestone[];
  columns: MilestoneBoardColumn[];
  /**
   * True when the project holds more tasks than one board will fetch. The
   * columns then carry the first page of cards while `total` and `done` still
   * count everything, so the screen has to say so out loud - cards and headers
   * that disagree in silence are worse than a missing board.
   */
  truncated: boolean;
}

/**
 * The board for one project.
 *
 * `withTasks=1` is what separates this from a plain milestone list; without it
 * the endpoint answers `{ rows }` and nothing else. Spelled as "1" rather than
 * "true" because the route accepts both and the shorter one keeps the URL
 * readable in a network log.
 */
export const getMilestoneBoard = (projectId: string) =>
  api.get<MilestoneBoardData>(`/admin/crm/projects/${projectId}/milestones?withTasks=1`);
